import type { IsoTimestamp } from './common.js';
import type { LeadFunnel } from './leadList.js';
import type { BotLaunchStatus, BotReplyMode, BotVertical } from './leadBot.js';
import type { GoLiveCheck, SetupResult } from '../lib/kyckConnect/setupStatus.js';
import type { ServiceBadgeId } from '../lib/kyckConnect/services.js';

// KYCKCONNECT WIRE SHAPES (redesign Phase 1). Built field by field on the server; no phone number appears anywhere here.

/** `GET /api/kyckconnect/accounts/:accountId/setup`: the services, the six steps, the warnings and the go-live checks. */
export interface KyckConnectSetupResponse extends SetupResult {
  accountId: string;
  accountName: string;
  /** The bot the club uses (the settings' chosen bot, else its active one): the Setup step 3 editor opens on it. */
  botId: string | null;
  botVertical: BotVertical | null;
  /** The club's industry, which sets the default vertical for a NEW bot. */
  accountIndustry: string | null;
  /** The club's number pool and the bot's own sender number (our numbers, not a lead's). Null when none. */
  pool: { name: string; numbers: string[] } | null;
  botNumber: string | null;
  badges: Array<{ id: ServiceBadgeId; label: string }>;
  /** How many lead routes point at the club, and how many are active and live: the Services step's read-only relay card. */
  routes: { total: number; activeLive: number };
  /** The bot's launch status, reply mode and everything copied that a person has not yet checked. Null when the club has no bot. */
  launch: ClubLaunchView | null;
}

/** ⚠️ Copied setup waiting for a person ("Copied from <club>: check before going live"). Labels only, never any club text. */
export interface ReviewItems {
  sections: Array<{ key: string; label: string }>;
  promotions: Array<{ id: string; title: string }>;
  followUps: boolean;
}

export interface ClubLaunchView {
  status: BotLaunchStatus;
  replyMode: BotReplyMode;
  /** Name of the club the setup was copied from, when any of it was. */
  copiedFromName: string | null;
  review: ReviewItems;
}

export const SUMMARY_DAYS = [7, 30, 90] as const;
export type SummaryDays = (typeof SUMMARY_DAYS)[number];

/** `GET /api/kyckconnect/accounts/:accountId/summary?days=`: the Dashboard sub-tab's key figures. Test leads are not counted. */
export interface KyckConnectSummaryResponse {
  days: SummaryDays;
  funnel: LeadFunnel;
  /** Leads flagged "needs a person" right now, whatever their age. */
  needsPerson: number;
}

export type { GoLiveCheck };

// ---- PHASE 4: THE CLUB'S SETUP IN THE PORTAL ----------------------------------------------------------------------------------
// ⚠️ Built field by field on the server. No pool, no bot number, no warnings, no go-live checks, no bot instructions, caps,
// allowlist or model: the Kyck-only steps are not in this shape at all.

export interface PortalSetupStep {
  id: 'services' | 'profile' | 'relay' | 'aiSms' | 'speedToLead';
  label: string;
  status: 'done' | 'incomplete' | 'off';
  detail: string;
  visible: boolean;
  hiddenMessage: string | null;
  editable: boolean;
  shared: boolean;
}

export interface PortalSetupResponse {
  accountId: string;
  accountName: string;
  /** The viewer holds `kyckconnect_setup`. Without it every step reads, none saves. */
  canEdit: boolean;
  services: { leadRelay: boolean; aiSms: { on: boolean; botName: string | null }; speedToLead: boolean };
  steps: PortalSetupStep[];
  /** Read-only relay summary: forms connected and CRM delivery over the last 30 days. Null when the club has no lead route. */
  relay: { routes: number; live: number; delivered: number; failed: number; inProgress: number } | null;
  /** Read-only AI SMS summary. Null when the club has no bot. `canEditPromotions` follows the bot's discussPromotions capability. */
  aiSms: {
    botName: string | null;
    textsOnItsOwn: boolean;
    capabilities: Array<{ key: string; label: string; on: boolean }>;
    canEditPromotions: boolean;
    /** What the club may edit about its assistant. ⚠️ Never the Kyck instructions, caps, number, mode, vertical or playbook. */
    settings: PortalAssistantSettings;
  } | null;
  /** Launch status, reply mode and copied setup awaiting the club's check. Null when the club has no bot. */
  launch: ClubLaunchView | null;
}

export interface PortalAssistantSettings {
  assistantName: string;
  capabilities: import('./leadBot.js').BotCapabilities;
  /** Whether Kyck has set a booking calendar: "Book tours" can be turned on only with one. */
  hasBookingCalendar: boolean;
  followUps: { noResponse: import('./leadBot.js').FollowUpStep[]; incomplete: import('./leadBot.js').FollowUpStep[]; needsReview: boolean } | null;
  handoff: { name: string; phone: string; email: string };
  testNumbers: string[];
}

/** `GET /api/portal/kyckconnect/clubs/:accountId/speed-to-lead`: the call list and the club's own numbers and messages. */
export interface PortalSpeedToLeadView {
  accountId: string;
  callsOn: boolean;
  rows: import('./speedToLead.js').StaffRow[];
  staffRingSeconds: number;
  roundTwoDelaySeconds: number;
  retryAfterMinutes: number | null;
  messages: { whisperMessage: string | null; missedCallMessage: string | null; outboundWhisperMessage: string | null; locked: boolean };
  bounds: { staffRingSeconds: { min: number; max: number }; roundTwoDelaySeconds: { min: number; max: number }; retryAfterMinutes: { min: number; max: number } };
}
